import { callAPI } from '@/api/httpservice';
import type { AthleteStatusResponse, StatsData } from '@/types/admin.types';
import type { Enveloppe } from '@/types/file.types';
import type { ActionData, User, UserActionData, UserData } from '@/types/user.types';

export function fetchStats(
	date: string,
	cluster: string,
	site: string,
	discipline: string,
	noc: string,
	process: string,
) {
	return callAPI<StatsData>(
		'GET',
		`/stats?date=${date}&cluster=${cluster}&site=${site}&discipline=${discipline}&noc=${noc}&process=${process}`,
		null,
	);
}

export function fetchUserData(page: number, limit: number, search: string, role:string) {
	return callAPI<{ items: User[]; total: number }>(
		'GET',
		`/users?page=${page}&limit=${limit}&search=${search}${role ? '&role='+role : ''}`,
		null,
	);
}

export function fetchUserActions(id:string,page: number, limit: number, search: string,) {
	return callAPI<UserActionData>(
		'GET',
		`/actions/${id}?page=${page}&limit=${limit}&search=${search}`,
		null,
	);
}

export function fetchActions(
  page: number,
  limit: number,
  orderBy?: string,
  date?: string,
  role?: string,
  action?: string,
  search?: string
) {
  const params = new URLSearchParams();

  // Obligatoires
  params.append('page', page.toString());
  params.append('limit', limit.toString());

  // Optionnels
  if (date) params.append('date', date);
  if (role) params.append('role', role);
  if (orderBy) params.append('orderBy', orderBy);
  if (search) params.append('search', search);
  if (action) params.append('actionType', action);

  return callAPI<ActionData>('GET', `/actions?${params.toString()}`, null);
}

export function createUser(email: string, roles: string[], ftpCreation?: boolean) {
	const body: Record<string, any> = {
		email,
		roles
	};

	if (ftpCreation) {
		body.createFtpAccount = true;
	}

	return callAPI<UserData>('POST', '/users', body);
}

export function deleteUser(id: string) {
	return callAPI<User>('DELETE', `/users/${id}`, null);
}

export function updateUser(
  id: string,
  roles: string[],
  ftpCreation?: boolean
) {
  const body: { roles: string[]; ftpEnabled?: boolean } = {
    roles,
  };

  // On inclut createFtpAccount uniquement si ftpCreation est défini
  if (ftpCreation !== undefined) {
    body.ftpEnabled = ftpCreation;
  }

  return callAPI<User>('PATCH', `/users/${id}`, body);
}

export function userFirstConnect(id: string, userName: string) {
	return callAPI<User>('PATCH', `/users/${id}/first-connection`, {
		username: userName,
	});
}

export function linkAthlete(
	envelopeId: string,
	athleteId: string,
  disciplineCode: string,
) {
	return callAPI<Enveloppe>(
		'PATCH',
		`/envelopes/${envelopeId}/athletes/${athleteId}/${disciplineCode}/link`,
		{ athleteId },
	);
}

export function switchAthlete(
	envelopeId: string,
	athleteId1: string,
  disciplineCode1: string,
	athleteId2: string,
  discplineCode2: string,
) {
	return callAPI<Enveloppe>(
		'PUT',
		`/envelopes/${envelopeId}/athletes/${athleteId1}/${disciplineCode1}/switch/${athleteId2}/${discplineCode2}`,
		{ athleteId1, athleteId2 },
	);
}

export function reportUnknownAthlet(envId:string){
    return callAPI<Enveloppe>("PATCH", `/envelopes/${envId}/athletes/unlink`, null);
}

export function fetchAthleteStatus(
  page: number,
  limit: number,
  search: string,
  sortOrder: string,
  dateFilter: string,
  status: string,
  country: string,
  discipline: string
) {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    search,
  });

  if (sortOrder) params.append("orderBy", sortOrder);
  if (dateFilter) params.append("timeAgo", dateFilter);
  if (status) params.append("status", status);
  if (country) params.append("countryCode", country);
  if (discipline) params.append("disciplineCode", discipline);

  return callAPI<AthleteStatusResponse>(
    "GET",
    `/envelopes/with-history?${params.toString()}`,
    null
  );
}

export function sendAccess(id: string) {
	return callAPI<User>('POST', `/users/${id}/resend-access-link`, null);
}

export function scanAthlete(id: string, disciplineCode?: string) {
	return callAPI<void>('POST', `/qr-code`, {athleteId:id, disciplineCode});
}

export function unscanAthlete(id: string, disciplineCode?: string) {
	return callAPI<void>('POST', `/qr-code/unscan`, {athleteId:id, disciplineCode});
}

export function prioritizeAthlete(id: string, disciplineCode: string) {
	return callAPI<User>('PATCH', `/athletes/${id}/${disciplineCode}/toggle-priority`, null);
}

export function passFolderToManualIntervention(id: string) {
  return callAPI<Enveloppe>('PATCH', `/envelopes/${id}/manual-intervention`, null);
}

export function getCalendarData() {
  return callAPI<any>('GET', `/events`, null);
}